/**
 * Site Context Utility
 * Detects the domain context (Norwegian or Swedish) and provides
 * appropriate configuration based on the current site.
 */

import app_data from "@/constants/app";
import store from "@/store";

/**
 * Available site contexts
 */
export const SITE_CONTEXT = {
  NORWEGIAN: 'no',
  SWEDISH: 'se'
};

/**
 * Detects if the current site is Norwegian based on domain or locale
 * @returns {boolean} True if Norwegian site, false otherwise
 */
export function isNorwegianSite() {
  const hostname = window.location.hostname.toLowerCase();
  
  // Prioritize hostname check (more reliable than store state)
  // Match .no TLD or securcloud.securdia.no subdomain pattern
  if (hostname.endsWith('.no') || hostname.includes('.no')) {
    return true;
  }
  
  // Fallback to locale check from store
  const currentLocale = store.getters.getAppLocale || 'sv';
  return currentLocale === 'no';
}

/**
 * Detects if the current site is Swedish based on domain or locale
 * @returns {boolean} True if Swedish site, false otherwise
 */
export function isSwedishSite() {
  return !isNorwegianSite();
}

/**
 * Gets the current site context
 * @returns {string} Site context identifier ('no' or 'se')
 */
export function getSiteContext() {
  return isNorwegianSite() ? SITE_CONTEXT.NORWEGIAN : SITE_CONTEXT.SWEDISH;
}

/**
 * Gets the default locale based on site context
 * @returns {string} Default locale code ('no' or 'sv')
 */
export function getDefaultLocaleByContext() {
  return isNorwegianSite() ? 'no' : 'sv';
}

/**
 * Gets the appropriate contact information based on site context
 * @returns {Object} Contact information object for current site
 */
export function getContactInfo() {
  return isNorwegianSite() 
    ? app_data.contact_info_no 
    : app_data.contact_info_se;
}

/**
 * Gets the agreement number mask based on site context
 * @returns {string} Input mask format (e.g., 'NO9999-9999' or 'SE9999-9999')
 */
export function getAgreementNumberMask() {
  return isNorwegianSite() ? 'NO####-####' : 'SE####-####';
}

/**
 * Gets the login URL based on current domain
 * Preserves the domain when redirecting to login page
 * @returns {string} Login URL path
 */
export function getLoginUrl() {
  return '/login';
}

/**
 * Gets the home URL based on current domain
 * Preserves the domain when redirecting to home page
 * @returns {string} Home URL path
 */
export function getHomeUrl() {
  return '/';
}

export default {
  SITE_CONTEXT,
  isNorwegianSite,
  isSwedishSite,
  getSiteContext,
  getDefaultLocaleByContext,
  getContactInfo,
  getAgreementNumberMask,
  getLoginUrl,
  getHomeUrl
};
